import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  forwardRef,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { NgSelectModule } from '@ng-select/ng-select';
import { RxIconModule } from '@nrwl/rx-icon';
import { createLocalDate, parseLocalDateString } from '../../../../helpers/date-utils';
import { BehaviorSubject } from 'rxjs';
import { SoapPlanItem } from '../soap-notes.service';
import { TextareaAutosizeDirective } from '../../shared/textarea-autosize-directive';
import { PasteStripDirective } from '../../shared/paste-strip-directive';
import { ScribeSuggestionIndicatorComponent } from '../../shared/scribe-suggestion-indicator/scribe-suggestion-indicator.component';
import { AmendmentFieldIndicatorComponent } from '../../shared/amendment-field-indicator/amendment-field-indicator.component';
import {
  ScribeSuggestionMarker,
  ScribeSuggestionMarkerTarget,
} from '../../../scribe-suggestion-marker.model';
import {
  clearScribeSuggestionMarker,
  copyScribeSuggestionMarker,
  isScribeSuggestionMarked,
} from '../../../scribe-suggestion-marker.util';

export type InputField = {
  title: string;
  value: string | number;
  placeholder?: string;
  required?: boolean;
  type?: string;
  hasConflict?: boolean;
  conflictTooltip?: string;
  id?: string;
};

export type SoapInputRow = {
  fields: InputField[];
  id?: number | string;
  isScribeSuggested?: ScribeSuggestionMarker;
};

type PlanItemView = {
  name: string;
  quantity: string;
};

@Component({
  selector: 'fb-soap-input-list',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatNativeDateModule,
    NgSelectModule,
    RxIconModule,
    TextareaAutosizeDirective,
    PasteStripDirective,
    ScribeSuggestionIndicatorComponent,
    AmendmentFieldIndicatorComponent,
  ],
  templateUrl: './fb-soap-input-list.html',
  styleUrls: ['./fb-soap-input-list.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FbSoapInputListComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FbSoapInputListComponent implements ControlValueAccessor, OnInit {
  @Input() label = '';
  @Input() fields: InputField[] = [];
  @Input()
  set readonly(value: boolean) {
    this._readonly = value;
    this.updateFormDisabledState();
  }
  get readonly(): boolean {
    return this._readonly;
  }
  @Input() addButtonText = '';
  @Input() populatedFrom = '';
  @Input() showExtraButton = false;
  @Input() extraButtonText = '';
  @Input() hideActionButtons = false;
  @Input() sectionMarkerTarget: ScribeSuggestionMarkerTarget | null = null;
  @Input() rowMarkerTargets: ScribeSuggestionMarkerTarget[] = [];
  @Input() attributesMarkerKey = '';
  /**
   * SOAP section this list edits, used to key its fields for the amendment indicator.
   */
  @Input() amendmentSection = '';
  @Input()
  set printMode(value: boolean) {
    this._printMode = !!value;
  }
  get printMode(): boolean {
    return this._printMode;
  }
  @Input() showEmptyRowsInPrint = false;
  @Input()
  set autoDate(value: string) {
    this._autoDate = value ?? '';
    this.applyAutoDateDefaults();
  }
  get autoDate(): string {
    return this._autoDate;
  }
  @Input() autoEmployeeId = '';
  @Input() lockDateEmployee = false;
  @Input()
  set planItems(value: SoapPlanItem[] | null | undefined) {
    this._planItems = Array.isArray(value) ? [...value] : [];
    this.updatePlanItemsView();
  }
  get planItems(): SoapPlanItem[] {
    return this._planItems;
  }
  @Input()
  set planItemsQuantity(value: unknown) {
    this._planItemsQuantity = value;
    this.updatePlanItemsView();
  }
  get planItemsQuantity(): unknown {
    return this._planItemsQuantity;
  }
  @Input()
  set assignedAttributes(value: string[] | null | undefined) {
    this._assignedAttributes = this.normalizeAttributes(value);
    this.cd.markForCheck();
  }
  get assignedAttributes(): string[] {
    return this._assignedAttributes;
  }

  @Input()
  set assignedAttributesDisplay(value: string[] | null | undefined) {
    this._assignedAttributesDisplay = this.normalizeAttributes(value);
    this.cd.markForCheck();
  }
  get assignedAttributesDisplay(): string[] {
    return this._assignedAttributesDisplay;
  }
  @Input()
  set expanded(value: boolean | string | null | undefined) {
    this._expanded = value === true || value === 'true';
    this.isExpanded = this._expanded;
    this.cd.markForCheck();
  }
  get expanded(): boolean {
    return this._expanded;
  }

  @Output() extraAction = new EventEmitter<void>();
  @Output() addAttributeAction = new EventEmitter<void>();
  @Output() assignedAttributesChange = new EventEmitter<string[]>();

  values: SoapInputRow[] = [];
  _disabled = false;
  private _readonly = false;
  isExpanded = false;
  private _expanded = false;
  private isWritingValue = false;
  planItemsView: PlanItemView[] = [];
  private _planItems: SoapPlanItem[] = [];
  private _planItemsQuantity: unknown = null;
  private _assignedAttributes: string[] = [];
  private _assignedAttributesDisplay: string[] = [];
  private _autoDate = '';
  private _printMode = false;
  form: FormGroup;

  @Input() employees$ = new BehaviorSubject<Array<{ text: string; value: string }>>([]);

  customSearchFn = (term: string, item: { text?: string; value?: string }) => {
    const query = (term || '').toLowerCase();
    if (!query) {
      return true;
    }

    const text = (item?.text || '').toLowerCase();
    const value = (item?.value || '').toLowerCase();
    return text.includes(query) || value.includes(query);
  };

  priorities = [
    { text: 'Low', value: 0 },
    { text: 'Medium', value: 1 },
    { text: 'High', value: 2 },
  ];

  private onChange: any = () => void 0;
  private onTouch: any = () => void 0;

  constructor(
    private cd: ChangeDetectorRef,
    private fb: FormBuilder,
  ) {
    this.form = this.fb.group({
      rows: this.fb.array([]),
    });
  }

  ngOnInit(): void {
    this.form.valueChanges.subscribe(() => {
      if (this.isWritingValue) {
        return;
      }
      this.emitValues();
    });
  }

  private get rows(): FormArray {
    return this.form.get('rows') as FormArray;
  }

  writeValue(value: any): void {
    this.isWritingValue = true;

    if (value && Array.isArray(value) && value.length > 0) {
      this.values = this.normalizeRows(value);
    } else {
      this.values = [this.createEmptyRow()];
    }

    this.rebuildRows();
    this.isWritingValue = false;
    this.cd.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabled = isDisabled;
    this.updateFormDisabledState();
  }

  addRow(): void {
    const row = this.createEmptyRow();
    this.values = [...this.values, row];
    this.rows.push(this.createRowGroup(row.fields));
    this.emitValues();
  }

  removeRow(index: number): void {
    if (this.values.length > 1) {
      this.values = this.values.filter((_, i) => i !== index);
      this.rows.removeAt(index);
      this.emitValues();
      return;
    }

    const row = this.values[0];
    clearScribeSuggestionMarker(row);
    const payloadRow = this.rowMarkerTargets[0];
    if (payloadRow && payloadRow !== row) {
      clearScribeSuggestionMarker(payloadRow);
    }
    row.fields = row.fields.map((field) => ({ ...field, value: '' }));
    row.id = undefined;

    row.fields.forEach((_field, fieldIndex) => {
      this.getControl(0, fieldIndex)?.setValue('');
    });

    this.emitValues();
  }

  onDateChange(rowIndex: number, fieldIndex: number, value: any): void {
    this.clearFieldScribeSuggestionMarker(rowIndex, fieldIndex);
    const dateStr = value ? parseLocalDateString(value) : '';
    const nextValue = !dateStr && this._autoDate ? this._autoDate : dateStr;
    const nextControlValue = nextValue
      ? (createLocalDate(parseLocalDateString(nextValue)) ?? '')
      : '';
    this.getControl(rowIndex, fieldIndex)?.setValue(nextControlValue);
  }

  onFieldEdited(rowIndex: number, fieldIndex: number): void {
    if (!this.clearFieldScribeSuggestionMarker(rowIndex, fieldIndex)) {
      return;
    }

    this.emitValues();
  }

  private clearFieldScribeSuggestionMarker(rowIndex: number, fieldIndex: number): boolean {
    const row = this.values[rowIndex];
    const field = row?.fields?.[fieldIndex];
    if (!row || !field) {
      return false;
    }

    const fieldKey = this.getMarkerFieldKey(field);
    clearScribeSuggestionMarker(row, fieldKey);
    const payloadRow = this.rowMarkerTargets[rowIndex];
    if (payloadRow && payloadRow !== row) {
      clearScribeSuggestionMarker(payloadRow, fieldKey);
    }
    return true;
  }

  getRowMarkerTarget(row: SoapInputRow, rowIndex: number): ScribeSuggestionMarkerTarget {
    return this.rowMarkerTargets[rowIndex] ?? row;
  }

  isScribeSuggestionUsed(
    target: ScribeSuggestionMarkerTarget | null | undefined,
    fieldKey: string,
  ): boolean {
    return isScribeSuggestionMarked(target, fieldKey);
  }

  isAnyScribeSuggestionUsed(
    target: ScribeSuggestionMarkerTarget | null | undefined,
    fieldKeys: string[],
  ): boolean {
    return fieldKeys.some((fieldKey) => isScribeSuggestionMarked(target, fieldKey));
  }

  onEmptyRowClick(index: number, event: any): void {
    if (index === 0 && this.isRowEmpty(this.values[index])) {
      const input = event.target.querySelector('input, textarea');
      if (input) {
        input.focus();
      }
    }
  }

  isRowEmpty(row: SoapInputRow): boolean {
    return row.fields.every((field) => !field.value || field.value === '');
  }

  isRowMeaningfulForPrint(row: SoapInputRow): boolean {
    if (!row || !Array.isArray(row.fields)) {
      return false;
    }

    return row.fields.some((field) => {
      if (!field || field.type === 'date' || field.type === 'employee') {
        return false;
      }

      const value = field.value;
      if (value === null || value === undefined) {
        return false;
      }

      if (field.type === 'priority') {
        if (typeof value === 'number') {
          return value >= 0;
        }
        const normalized = String(value).trim();
        return normalized !== '' && normalized !== '-1';
      }

      if (typeof value === 'string') {
        return value.trim() !== '';
      }

      if (Array.isArray(value)) {
        return value.length > 0;
      }

      return true;
    });
  }

  isNameField(title: string): boolean {
    return title.toLowerCase() === 'name';
  }

  isTextFieldType(type?: string): boolean {
    return !type || !['date', 'employee', 'priority', 'textarea'].includes(type);
  }

  isHiddenField(type?: string): boolean {
    return ['plan-fields', 'plan-items', 'plan-items-quantity'].includes(type ?? '');
  }

  getControl(rowIndex: number, fieldIndex: number): FormControl | null {
    return this.rows.at(rowIndex)?.get(this.controlName(fieldIndex)) as FormControl | null;
  }

  addAttribute(): void {
    this.addAttributeAction.emit();
  }

  addPlanItems(): void {
    this.extraAction.emit();
  }

  getAttributeLabel(): string {
    const base = this.addButtonText || this.label || 'Note';
    return base.replace(/\s+note$/i, '');
  }

  toggleExpanded(): void {
    this.isExpanded = !this.isExpanded;
    this.cd.markForCheck();
  }

  private createEmptyRow(): SoapInputRow {
    return {
      fields: this.fields.map((field) => ({
        title: field.title,
        value: this.getDefaultValue(field),
        placeholder: field.placeholder,
        type: field.type,
      })),
    };
  }

  private normalizeRows(value: any): SoapInputRow[] {
    const first = value[0];
    if (Array.isArray(first)) {
      return value.map((row: InputField[]) => {
        const result: SoapInputRow = {
          fields: this.normalizeRowFields(row),
          id: undefined,
        };
        copyScribeSuggestionMarker(row as unknown as ScribeSuggestionMarkerTarget, result);
        return result;
      });
    }

    return value.map((row: SoapInputRow) => {
      const result: SoapInputRow = {
        fields: this.normalizeRowFields(row.fields),
        id: row.id,
      };
      copyScribeSuggestionMarker(row, result);
      return result;
    });
  }

  private normalizeRowFields(rowFields?: InputField[]): InputField[] {
    return this.fields.map((field, index) => {
      const rawValue = rowFields?.[index]?.value;
      if (field.type === 'date') {
        const normalizedDate =
          rawValue === null || rawValue === undefined || rawValue === ''
            ? ''
            : parseLocalDateString(rawValue as any);

        if (!normalizedDate) {
          return {
            ...field,
            value: this.getDefaultValue(field),
          };
        }

        return {
          ...field,
          value: normalizedDate,
        };
      }

      if (rawValue === null || rawValue === undefined || rawValue === '') {
        return {
          ...field,
          value: this.getDefaultValue(field),
        };
      }
      return {
        ...field,
        value: rawValue ?? this.getDefaultValue(field),
      };
    });
  }

  private serializeRows(rows: SoapInputRow[]): SoapInputRow[] {
    return rows.map((row) => {
      const result: SoapInputRow = {
        fields: row.fields.map((field) => ({ ...field })),
        id: row.id,
      };
      copyScribeSuggestionMarker(row, result);
      return result;
    });
  }

  private rebuildRows(): void {
    this.rows.clear();

    this.values.forEach((row) => {
      this.rows.push(this.createRowGroup(row.fields));
    });

    this.updateFormDisabledState();
    this.form.updateValueAndValidity({ emitEvent: false });
  }

  private createRowGroup(rowFields: InputField[]): FormGroup {
    const group = this.fb.group({});
    this.fields.forEach((_field, index) => {
      const field = this.fields[index];
      const rawValue = rowFields[index]?.value ?? '';
      const value =
        field?.type === 'date'
          ? (createLocalDate(parseLocalDateString(rawValue as any)) ?? '')
          : rawValue;
      const shouldDisable =
        this._disabled ||
        this._readonly ||
        this.isNameField(this.fields[index]?.title ?? '') ||
        this.shouldLockField(field);
      group.addControl(
        this.controlName(index),
        new FormControl({ value, disabled: shouldDisable }),
      );
    });
    return group;
  }

  private emitValues(): void {
    this.syncValuesFromForm();
    this.onChange(this.serializeRows(this.values));
    this.onTouch();
    this.cd.markForCheck();
  }

  private syncValuesFromForm(): void {
    this.values = this.rows.controls.map((rowGroup, rowIndex) => {
      const current = this.values[rowIndex] ?? this.createEmptyRow();
      return {
        ...current,
        id: current.id,
        fields: this.fields.map((field, fieldIndex) => {
          const controlValue = rowGroup.get(this.controlName(fieldIndex))?.value ?? '';
          const value =
            field.type === 'date' ? parseLocalDateString(controlValue as any) : controlValue;
          return {
            ...field,
            value,
          };
        }),
      };
    });
  }

  private getMarkerFieldKey(field: InputField): string {
    return field.type === 'textarea' ? 'note' : (field.type ?? field.title);
  }

  private controlName(fieldIndex: number): string {
    return `f${fieldIndex}`;
  }

  private updateFormDisabledState(): void {
    if (!this.form) {
      return;
    }

    const disableAll = this._disabled || this._readonly;

    this.rows.controls.forEach((rowGroup) => {
      this.fields.forEach((field, fieldIndex) => {
        const control = rowGroup.get(this.controlName(fieldIndex));
        if (!control) {
          return;
        }

        const shouldDisable =
          disableAll || this.isNameField(field.title) || this.shouldLockField(field);
        if (shouldDisable && control.enabled) {
          control.disable({ emitEvent: false });
        } else if (!shouldDisable && control.disabled) {
          control.enable({ emitEvent: false });
        }
      });
    });

    this.cd.markForCheck();
  }

  private normalizeAttributes(value: string[] | null | undefined): string[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.map((item) => String(item).trim()).filter((item) => item);
  }

  private getDefaultValue(field: InputField): string {
    const type = field.type;
    if (type === 'date' && this._autoDate) {
      return this._autoDate;
    }
    if (type === 'employee' && this.autoEmployeeId) {
      return this.autoEmployeeId;
    }
    return '';
  }

  private applyAutoDateDefaults(): void {
    if (!this._autoDate || this.values.length === 0 || !this.form) {
      return;
    }

    let changed = false;
    this.values.forEach((row, rowIndex) => {
      row.fields.forEach((field, fieldIndex) => {
        if (field.type !== 'date') {
          return;
        }

        const current = field.value;
        if (current !== null && current !== undefined && String(current).trim() !== '') {
          return;
        }

        field.value = this._autoDate;
        const control = this.getControl(rowIndex, fieldIndex);
        if (control) {
          control.setValue(createLocalDate(parseLocalDateString(this._autoDate)) ?? '', {
            emitEvent: false,
          });
        }
        changed = true;
      });
    });

    if (changed) {
      this.emitValues();
    }
  }

  private shouldLockField(field: InputField): boolean {
    if (!this.lockDateEmployee) {
      return false;
    }
    return field.type === 'date' || field.type === 'employee';
  }

  getEmployeeLabel(value: unknown): string {
    const list = this.employees$?.value ?? [];
    const key = value === null || value === undefined ? '' : String(value);
    if (!key) {
      return '';
    }
    const match = list.find((item) => String(item.value) === key);
    return match?.text ?? key;
  }

  get displayAttributes(): string[] {
    return this._assignedAttributesDisplay.length > 0
      ? this._assignedAttributesDisplay
      : this._assignedAttributes;
  }

  private updatePlanItemsView(): void {
    this.planItemsView = this.normalizePlanItemsView();
    this.cd.markForCheck();
  }

  private normalizePlanItemsView(): PlanItemView[] {
    const items = Array.isArray(this._planItems) ? this._planItems : [];
    const quantities = Array.isArray(this._planItemsQuantity) ? this._planItemsQuantity : null;

    return items.map((item, index) => {
      return {
        name: item.name || item.id,
        quantity: this.getFallbackQuantity(quantities, index),
      };
    });
  }

  private getFallbackQuantity(quantities: unknown[] | null, index: number): string {
    if (quantities && quantities[index] !== undefined) {
      return this.asString(quantities[index]);
    }

    if (!quantities && this._planItemsQuantity !== null && this._planItemsQuantity !== undefined) {
      return this.asString(this._planItemsQuantity);
    }

    return '';
  }

  private asString(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }

    return String(value).trim();
  }
}
